import { SupportedLocale } from '@zep-us/i18n';
import { InitOptions } from 'i18next';
import { ChainedBackendOptions } from 'i18next-chained-backend';
import HttpBackend, { HttpBackendOptions } from 'i18next-http-backend';

type AppName = 'play' | 'edit' | 'web' | 'dashboard' | 'web3';

export const getLoadPathOursByAppName = (appName: string) => {
  const pathMap: Record<string, string> = {
    play: '/play/locales/{{lng}}/{{ns}}.json',
    edit: '/edit/locales/{{lng}}/{{ns}}.json',
    dashboard: '/dashboard/locales/{{lng}}/{{ns}}.json',
    web3: '/web3/locales/{{lng}}/{{ns}}.json',
  };
  return pathMap[appName] ?? '/locales/{{lng}}/{{ns}}.json';
};

const getZepBackendOptions = (appName: AppName) => ({
  loadPath: getLoadPathOursByAppName(appName),
  queryStringParams: {
    version: process.env.NEXT_PUBLIC_COMMIT_SHA,
  },
});

export const chainedBackendOptions = (appName: AppName) => {
  return {
    backends: [HttpBackend],
    backendOptions: [getZepBackendOptions(appName)],
  };
};

export const getDefaultI18nOptions = (payload: {
  appName: AppName;
  namespaces: string[];
  lng?: string;
  supportedLngs: readonly SupportedLocale[];
}): InitOptions<ChainedBackendOptions & HttpBackendOptions> => {
  const { lng, appName, namespaces: ns, supportedLngs } = payload;

  // 지역 코드를 포함하는 locale case 들 (zh-TH, zh-TW)
  const hasLocalesWithRegion = lng
    ? lng.includes('-') && lng.length > 2
    : false;

  return {
    debug: false,
    detection: {
      order: [
        'querystring',
        'navigator',
        'cookie',
        'htmlTag',
        'path',
        'subdomain',
      ],
    },
    backend: {
      ...chainedBackendOptions(appName),
    },
    supportedLngs,
    fallbackLng: false,
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
      bindI18n: 'languageChanged loaded',
    },
    ns,
    lng,
    // 중국어 같은 지역 구분이 필요한 언어가 있는 경우에만 false로 설정
    // If true will pass eg. en-US if finding en in supportedLngs
    nonExplicitSupportedLngs: !hasLocalesWithRegion,
    // 중국어 같은 지역 구분이 필요한 언어가 있는 경우 currentOnly 사용
    // 'en-US': 'all' --> ['en-US', 'en', 'dev'], 'currentOnly' --> 'en-US', 'languageOnly' --> 'en'
    load: hasLocalesWithRegion ? 'currentOnly' : 'languageOnly',
  };
};
